import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Loader2, Plus, Star, X } from 'lucide-react';
import { useTenantApiToken, useTenantProfile } from '../../contexts/TenantProfileContext';
import { useAutosave, type AutosaveControls } from '../../hooks/useAutosave';
import { useCardSaveBadges } from '../../hooks/useCardSaveBadges';
import { CardSaveBadge } from './CardSaveBadge';
import {
  finalizeProspectGroup,
  fetchProspectGroup,
  leaveProspectGroup,
  putProspectGroupDraft,
  putProspectGroupMemberEmail,
  putProspectGroupMemberPhone,
  putProspectGroupSoloDeclared,
  ProspectGroupApiError,
  removeProspectGroupMember,
  setProspectGroupLeadContact,
  type ProspectGroupPortalData,
  type ProspectGroupPortalMember,
} from '../../services/tenantApi';
import {
  formatPhoneForTenantPortalDisplay,
  formatPhoneInputLive,
  isCanadianPhone,
  loginPhoneKind,
  phoneInputMatchesE164,
} from '../../utils/phone-display';

function rowHasName(row: DraftRow): boolean {
  return Boolean(row.firstName.trim() || row.lastName.trim());
}

/** Status is never blank once a person has a name. The signed-in member is "You" until they are pending or accepted. */
function statusLabelForPerson(
  status: ProspectGroupPortalMember['status'] | null | undefined,
  options?: { isSelf?: boolean; hasName?: boolean },
): string | null {
  if (status === 'ACCEPTED') return 'Accepted';
  if (status === 'INVITED') return 'Pending';
  if (options?.isSelf) return 'You';
  if (options?.hasName === false) return null;
  if (status === 'DRAFT' || options?.hasName) return 'Unsent';
  return null;
}

/**
 * The group card is often two bento columns, so the viewport `md` breakpoint
 * is wider than the card. Measure the card and pick a layout from that width.
 * A single row is used only when each name can stay readable beside the phone.
 * Otherwise names share the first line, and phone, status, and lead wrap under them.
 */
type GroupTableMode = 'row' | 'split';

const GroupTableModeContext = createContext<GroupTableMode>('split');

function useGroupTableMode(): GroupTableMode {
  return useContext(GroupTableModeContext);
}

/** Below this width, stack fields on very narrow cards; mid-size keeps one row with tighter name columns. */
const ROW_LAYOUT_MIN_PX = 420;

/** Tailwind must see the full class string — do not build grid-cols with template literals. */
const GROUP_TABLE_GRID_ROW =
  'grid min-w-0 grid-cols-[minmax(3.75rem,0.95fr)_minmax(3.75rem,0.95fr)_minmax(6.25rem,1.15fr)_minmax(4.25rem,4.75rem)_2.75rem_2.25rem] items-start gap-x-1.5 sm:gap-x-2 sm:grid-cols-[minmax(4.5rem,1.1fr)_minmax(4.5rem,1.1fr)_minmax(7rem,1.2fr)_minmax(4.75rem,5rem)_3rem_2.5rem]';

function groupTableGrid(mode: GroupTableMode): string {
  if (mode === 'split') {
    return 'grid min-w-0 grid-cols-2 gap-x-2 gap-y-2.5';
  }
  return GROUP_TABLE_GRID_ROW;
}

const GROUP_ROW_CLASS =
  'flex flex-col gap-2 rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 p-3 md:rounded-none md:border-0 md:bg-transparent md:p-0';

const GROUP_FIELD_CLASS =
  'box-border h-10 w-full min-w-0 rounded-md border border-gray-300 dark:border-white/15 bg-white dark:bg-gray-800 px-2.5 text-sm leading-5 text-gray-900 dark:text-white';

const GROUP_FIELD_READONLY_CLASS =
  'box-border flex h-10 w-full min-w-0 items-center rounded-md border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-800 px-2.5 text-sm leading-5 text-gray-700 dark:text-gray-300 md:bg-gray-50';

function GroupPeople({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [mode, setMode] = useState<GroupTableMode>('split');

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const apply = (width: number) => {
      setMode(width >= ROW_LAYOUT_MIN_PX ? 'row' : 'split');
    };
    apply(node.clientWidth);
    const observer = new ResizeObserver((entries) => {
      apply(entries[0]?.contentRect.width ?? node.clientWidth);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <GroupTableModeContext.Provider value={mode}>
      <div ref={ref} className="min-w-0 space-y-3">
        {children}
      </div>
    </GroupTableModeContext.Provider>
  );
}

function LabeledCell({
  label,
  spanAll = false,
  children,
}: {
  label: string;
  wide?: boolean;
  spanAll?: boolean;
  children: ReactNode;
}) {
  const split = useGroupTableMode() === 'split';
  return (
    <div className={`min-w-0 ${split && spanAll ? 'col-span-2' : ''}`}>
      {split ? (
        <span className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">{label}</span>
      ) : null}
      {children}
    </div>
  );
}

function GroupColumnHeader() {
  const mode = useGroupTableMode();
  if (mode !== 'row') return null;
  return (
    <div
      className={`${GROUP_TABLE_GRID_ROW} text-xs font-medium leading-tight text-gray-500 dark:text-gray-400`}
    >
      <span>First name</span>
      <span>Last name</span>
      <span>Phone</span>
      <span>Status</span>
      <span>Lead contact</span>
      <span className="sr-only">Remove</span>
    </div>
  );
}

function GroupTableRow({ children, remove }: { children: ReactNode; remove?: ReactNode }) {
  const mode = useGroupTableMode();
  return (
    <div className={GROUP_ROW_CLASS}>
      <div className={groupTableGrid(mode)}>
        {children}
        {remove ?? <span aria-hidden="true" />}
      </div>
    </div>
  );
}

function StatusCell({
  label,
  onSend,
  sending,
}: {
  label: string | null;
  onSend?: () => void;
  sending?: boolean;
}) {
  return (
    <LabeledCell label="Status">
      {onSend ? (
        <button
          type="button"
          disabled={sending}
          onClick={onSend}
          className="box-border flex h-10 w-full min-w-0 items-center justify-center rounded-md bg-red-700 px-2 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
        >
          {sending ? 'Sending…' : 'Send'}
        </button>
      ) : (
        <div className={`${GROUP_FIELD_READONLY_CLASS} justify-center whitespace-nowrap text-center`}>
          {label ?? '\u00a0'}
        </div>
      )}
    </LabeledCell>
  );
}

function LeadContactCell({ isLead, onSetLead }: { isLead: boolean; onSetLead?: () => void }) {
  let control: ReactNode;
  if (isLead) {
    control = (
      <div className={`${GROUP_FIELD_READONLY_CLASS} justify-center`} aria-label="Lead contact">
        <Star className="h-4 w-4 fill-amber-500 text-amber-600 dark:text-amber-400" aria-hidden />
      </div>
    );
  } else if (!onSetLead) {
    control = <div className={GROUP_FIELD_READONLY_CLASS} aria-hidden />;
  } else {
    control = (
      <button
        type="button"
        onClick={onSetLead}
        aria-label="Set as lead contact"
        title="Set as lead contact"
        className="group box-border flex h-10 w-full min-w-0 items-center justify-center rounded-md border border-gray-300 dark:border-white/15 bg-white dark:bg-gray-800 px-3 text-gray-400 dark:text-gray-500 hover:border-gray-400 hover:text-amber-600"
      >
        <Star className="h-4 w-4 md:opacity-0 md:group-hover:opacity-100" aria-hidden />
      </button>
    );
  }

  return <LabeledCell label="Lead contact">{control}</LabeledCell>;
}

function RemoveFromGroupButton({ onClick, name }: { onClick: () => void; name: string }) {
  return (
    <LabeledCell label={'\u00a0'}>
      <button
        type="button"
        onClick={onClick}
        aria-label={`Remove ${name} from group`}
        title="Remove from group"
        className="box-border flex h-10 w-full min-w-0 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-red-700 dark:text-gray-400 dark:hover:bg-white/10 dark:hover:text-red-400"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </LabeledCell>
  );
}

interface DraftRow {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  canadianPhone: string;
  clientId: string;
}

function emptyRow(): DraftRow {
  return { firstName: '', lastName: '', phone: '', email: '', canadianPhone: '', clientId: crypto.randomUUID() };
}

function rowHasAnyContent(row: DraftRow): boolean {
  return Boolean(
    row.firstName.trim() || row.lastName.trim() || row.phone.replace(/\D/g, '').length > 0,
  );
}

function rowNeedsEmail(row: DraftRow): boolean {
  return phoneCannotBeTexted(row.phone) && !isCanadianPhone(row.canadianPhone);
}

function rowIsComplete(row: DraftRow): boolean {
  return Boolean(
    row.firstName.trim() &&
      row.lastName.trim() &&
      loginPhoneKind(row.phone) !== 'incomplete',
  );
}

function hasPartialInviteeRows(rows: DraftRow[]): boolean {
  return rows.some((row) => rowHasAnyContent(row) && !rowIsComplete(row));
}

function pendingInviteeMembers(data: ProspectGroupPortalData): ProspectGroupPortalMember[] {
  return (data.members ?? []).filter(
    (member) =>
      !member.isHostManager &&
      member.status !== 'REMOVED' &&
      (member.status === 'INVITED' || member.status === 'DRAFT'),
  );
}

function draftRowFromPortalInvite(row: ProspectGroupPortalData['draftInviteRows'][number]): DraftRow {
  return {
    firstName: row.firstName,
    lastName: row.lastName,
    phone: row.phone ? formatPhoneForTenantPortalDisplay(row.phone) : row.phone,
    email: row.email ?? '',
    canadianPhone: row.canadianPhone ?? '',
    clientId: row.clientId ?? crypto.randomUUID(),
  };
}

function inviteRowMatchesMemberName(
  row: { firstName: string; lastName: string },
  member: ProspectGroupPortalMember,
): boolean {
  const rowName = [row.firstName, row.lastName].filter(Boolean).join(' ').trim().toLowerCase();
  const memberName = member.displayName.trim().toLowerCase();
  return Boolean(rowName && memberName && rowName === memberName);
}

function storedInviteRowMatchesPendingMember(
  stored: ProspectGroupPortalData['draftInviteRows'][number],
  member: ProspectGroupPortalMember,
): boolean {
  if (inviteRowMatchesMemberName(stored, member)) return true;
  return Boolean(
    stored.phone &&
      member.primaryPhone &&
      phoneInputMatchesE164(stored.phone, member.primaryPhone),
  );
}

function draftInviteeRowsFromGroup(data: ProspectGroupPortalData): DraftRow[] {
  const pendingMembers = pendingInviteeMembers(data);

  if (data.status === 'ACTIVE') {
    const pendingMemberIds = new Set(pendingMembers.map((member) => member.id));
    const acceptedMembers = (data.members ?? []).filter(
      (member) => member.status === 'ACCEPTED' && !member.isHostManager,
    );
    const storedByMemberId = new Map<string, DraftRow>();

    for (const stored of data.draftInviteRows ?? []) {
      const clientId = stored.clientId?.trim();
      if (clientId && pendingMemberIds.has(clientId)) {
        storedByMemberId.set(clientId, draftRowFromPortalInvite(stored));
      }
    }

    const memberRows = pendingMembers.map(
      (member) => storedByMemberId.get(member.id) ?? memberToRow(member),
    );

    const inProgressRows = (data.draftInviteRows ?? [])
      .filter((stored) => {
        const clientId = stored.clientId?.trim();
        if (clientId && pendingMemberIds.has(clientId)) return false;
        const hasContent = Boolean(
          stored.firstName.trim() ||
            stored.lastName.trim() ||
            stored.phone.replace(/\D/g, '').length > 0,
        );
        if (!hasContent) return false;
        if (acceptedMembers.some((member) => storedInviteRowMatchesPendingMember(stored, member))) {
          return false;
        }
        return !pendingMembers.some((member) => storedInviteRowMatchesPendingMember(stored, member));
      })
      .map(draftRowFromPortalInvite);

    return [...memberRows, ...inProgressRows, emptyRow()];
  }

  const acceptedHousemates = (data.members ?? []).filter(
    (member) => !member.isHostManager && member.status === 'ACCEPTED',
  );
  const housemateRows = acceptedHousemates.map(memberToRow);

  const alreadyInvitedPhones = (data.members ?? [])
    .filter((member) => member.status === 'INVITED' || member.status === 'ACCEPTED')
    .map((member) => member.primaryPhone)
    .filter((phone): phone is string => Boolean(phone));
  const pendingDraftRows = (data.draftInviteRows ?? [])
    .filter(
      (row) =>
        !row.phone || !alreadyInvitedPhones.some((phone) => phoneInputMatchesE164(row.phone, phone)),
    )
    .filter((stored) => {
      const row = draftRowFromPortalInvite(stored);
      if (!rowHasAnyContent(row)) return false;
      if (rowIsComplete(row)) return true;
      if (acceptedHousemates.length === 0) return true;
      if (pendingInviteeMembers(data).some((member) => storedInviteRowMatchesPendingMember(stored, member))) {
        return true;
      }
      return false;
    })
    .map(draftRowFromPortalInvite);

  if (housemateRows.length > 0 || pendingDraftRows.length > 0) {
    return [...housemateRows, ...pendingDraftRows, emptyRow()];
  }

  const invitees = (data.members ?? []).filter(
    (m) => !m.isHostManager && m.status !== 'REMOVED',
  );
  return invitees.length > 0 ? invitees.map(memberToRow) : [emptyRow()];
}

function inviteRowsForPersist(rows: DraftRow[], group: ProspectGroupPortalData | null) {
  const pendingMembers = group ? pendingInviteeMembers(group) : [];
  const pendingMemberIds = new Set(pendingMembers.map((member) => member.id));
  const slotted = new Map<string, DraftRow>();

  for (const row of rows.filter((entry) => rowHasAnyContent(entry))) {
    if (pendingMemberIds.has(row.clientId)) {
      slotted.set(row.clientId, row);
    }
  }

  const result: DraftRow[] = [...slotted.values()];
  for (const row of rows.filter((entry) => rowHasAnyContent(entry))) {
    if (pendingMemberIds.has(row.clientId)) continue;
    const duplicateSlotted = pendingMembers.some(
      (member) =>
        slotted.has(member.id) && inviteRowMatchesMemberName(row, member),
    );
    if (duplicateSlotted) continue;
    result.push(row);
  }

  return result.map((row) => ({
    firstName: row.firstName.trim(),
    lastName: row.lastName.trim(),
    phone: row.phone.trim(),
    email: row.email.trim(),
    canadianPhone: row.canadianPhone.trim(),
    clientId: row.clientId,
  }));
}

function serializeInviteRowsSnapshot(
  rows: DraftRow[],
  group: ProspectGroupPortalData | null = null,
): string {
  return JSON.stringify(inviteRowsForPersist(rows, group));
}

function applyGroupDraftState(
  data: ProspectGroupPortalData,
  setGroup: (g: ProspectGroupPortalData) => void,
  setDraftRows: (rows: DraftRow[]) => void,
): void {
  setGroup(data);
  if (data.canEditDraft ?? (data.status === 'DRAFT' && data.isHostManager)) {
    setDraftRows(draftInviteeRowsFromGroup(data));
  }
}

function memberMatchingDraftRow(
  group: ProspectGroupPortalData | null,
  row: DraftRow,
): ProspectGroupPortalMember | undefined {
  if (!group) return undefined;
  const byClientId = group.members.find(
    (member) => member.id === row.clientId && member.status !== 'REMOVED',
  );
  if (byClientId) return byClientId;
  if (!row.phone.trim()) return undefined;
  return group.members.find(
    (member) =>
      member.status !== 'REMOVED' &&
      Boolean(member.primaryPhone) &&
      phoneInputMatchesE164(row.phone, member.primaryPhone ?? ''),
  );
}

function memberToRow(member: ProspectGroupPortalMember): DraftRow {
  const parts = member.displayName.trim().split(/\s+/);
  const firstName = parts[0] ?? '';
  const lastName = parts.slice(1).join(' ') || '';
  const rawPhone = member.primaryPhone ?? '';
  return {
    firstName,
    lastName,
    phone: rawPhone ? formatPhoneForTenantPortalDisplay(rawPhone) : '',
    email: member.primaryEmail ?? '',
    canadianPhone: '',
    clientId: member.id,
  };
}

function applyProspectGroupErrorToRows(
  err: unknown,
  draftRows: DraftRow[],
): { sectionError: string | null; rowErrors: Record<string, string> } {
  if (!(err instanceof ProspectGroupApiError)) {
    return {
      sectionError: err instanceof Error ? err.message : 'Something went wrong.',
      rowErrors: {},
    };
  }
  if (err.phoneE164) {
    const row = draftRows.find((r) => phoneInputMatchesE164(r.phone, err.phoneE164!));
    if (row) {
      return { sectionError: null, rowErrors: { [row.clientId]: err.message } };
    }
  }
  if (err.code === 'ALREADY_IN_GROUP') {
    const duplicateNames = draftRows.filter((row, index, all) =>
      all.some(
        (other, otherIndex) =>
          otherIndex !== index &&
          row.firstName.trim().toLowerCase() === other.firstName.trim().toLowerCase() &&
          row.lastName.trim().toLowerCase() === other.lastName.trim().toLowerCase() &&
          Boolean(row.firstName.trim() || row.lastName.trim()),
      ),
    );
    if (duplicateNames.length > 0) {
      const rowErrors: Record<string, string> = {};
      for (const row of duplicateNames) {
        rowErrors[row.clientId] = err.message;
      }
      return { sectionError: null, rowErrors };
    }
  }
  return { sectionError: err.message, rowErrors: {} };
}

function splitDisplayName(displayName: string | null | undefined): { firstName: string; lastName: string } {
  const parts = (displayName ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: '', lastName: '' };
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
}

function SmsBlockedNotice() {
  const detail = 'We cannot send text notifications to users with international phone numbers.';
  return (
    <p className="text-sm text-amber-800 dark:text-amber-200" title={detail}>
      <span aria-hidden="true">⚠️ </span>
      We can&apos;t text this number.
      <span className="sr-only"> {detail}</span>
    </p>
  );
}

function phoneCannotBeTexted(phone: string): boolean {
  const digits = phone.replace(/\D/g, '');
  if (digits.length < 10) return false;
  return !isCanadianPhone(phone);
}

function PersonReadOnlyFields({
  firstName,
  lastName,
  phone,
  name,
  hidePhone = false,
}: {
  firstName: string;
  lastName: string;
  phone: string;
  name: string;
  hidePhone?: boolean;
}) {
  const phoneDisplay = phone ? formatPhoneForTenantPortalDisplay(phone) : '—';
  return (
    <>
      <LabeledCell label="First name" wide>
        <input
          type="text"
          readOnly
          value={firstName || '—'}
          aria-label={`${name} first name`}
          className={`${GROUP_FIELD_READONLY_CLASS} truncate`}
        />
      </LabeledCell>
      <LabeledCell label="Last name" wide>
        <input
          type="text"
          readOnly
          value={lastName || '—'}
          aria-label={`${name} last name`}
          className={`${GROUP_FIELD_READONLY_CLASS} truncate`}
        />
      </LabeledCell>
      {hidePhone ? null : (
      <LabeledCell label="Phone" wide spanAll>
        <input
          type="text"
          readOnly
          value={phoneDisplay}
          aria-label={`${name} phone`}
          className={`${GROUP_FIELD_READONLY_CLASS} tabular-nums`}
        />
      </LabeledCell>
      )}
    </>
  );
}

const CONTACT_TOGGLE_CLASS =
  'box-border inline-flex h-10 shrink-0 items-center rounded-md border border-gray-300 bg-white px-2.5 text-xs font-semibold text-gray-800 hover:bg-gray-50 dark:border-white/15 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-white/5';

function phonesForMember(member: ProspectGroupPortalMember): string[] {
  if (member.phones && member.phones.length > 0) return member.phones;
  return member.primaryPhone ? [member.primaryPhone] : [];
}

function canadianPhoneFrom(phones: string[]): string | null {
  return phones.find((phone) => isCanadianPhone(phone)) ?? null;
}

function UntestablePhoneContact({
  name,
  phones,
  emailValue,
  onEmailChange,
  onEmailCommit,
  canadianDraft,
  onCanadianDraftChange,
  onCanadianCommit,
  readOnly = false,
}: {
  name: string;
  phones: string[];
  emailValue: string;
  onEmailChange: (value: string) => void;
  onEmailCommit: () => void;
  canadianDraft: string;
  onCanadianDraftChange: (value: string) => void;
  onCanadianCommit: () => void;
  readOnly?: boolean;
}) {
  const canadian = canadianPhoneFrom(phones);
  const blockedPhone = phones.find((phone) => phoneCannotBeTexted(phone)) ?? null;
  const shownPhone = canadian ?? blockedPhone ?? phones[0] ?? '';
  const needsAlternate = Boolean(blockedPhone) && !canadian;
  const [mode, setMode] = useState<'phone' | 'email'>('phone');
  const phoneDisplay = shownPhone ? formatPhoneForTenantPortalDisplay(shownPhone) : '—';

  if (!needsAlternate || readOnly) {
    return (
      <LabeledCell label="Phone" wide spanAll>
        <input
          type="text"
          readOnly
          value={phoneDisplay}
          aria-label={`${name} phone`}
          className={`${GROUP_FIELD_READONLY_CLASS} tabular-nums`}
        />
        {needsAlternate ? <div className="mt-1"><SmsBlockedNotice /></div> : null}
      </LabeledCell>
    );
  }

  return (
    <LabeledCell label={mode === 'email' ? 'Email' : 'Phone'} wide spanAll>
      <div className="flex items-start gap-1">
        <div className="min-w-0 flex-1 space-y-1">
          {mode === 'phone' ? (
            <>
              <input
                type="text"
                readOnly
                value={phoneDisplay}
                aria-label={`${name} phone`}
                className={`${GROUP_FIELD_READONLY_CLASS} tabular-nums`}
              />
              <SmsBlockedNotice />
              <input
                placeholder="Canadian number"
                aria-label="Canadian phone number"
                inputMode="tel"
                autoComplete="tel"
                value={canadianDraft}
                onChange={(event) => onCanadianDraftChange(formatPhoneInputLive(event.target.value))}
                onBlur={onCanadianCommit}
                className={`${GROUP_FIELD_CLASS} tabular-nums`}
              />
            </>
          ) : (
            <>
              <input
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="Email"
                aria-label="Email"
                value={emailValue}
                onChange={(event) => onEmailChange(event.target.value)}
                onBlur={onEmailCommit}
                className={GROUP_FIELD_CLASS}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400">We'll email the group invite to this address.</p>
            </>
          )}
        </div>
        <button
          type="button"
          className={CONTACT_TOGGLE_CLASS}
          onClick={() => setMode((current) => (current === 'phone' ? 'email' : 'phone'))}
        >
          {mode === 'phone' ? 'Email' : 'Phone'}
        </button>
      </div>
    </LabeledCell>
  );
}

function MemberUntestableContact({
  member,
  onSaved,
  onError,
}: {
  member: ProspectGroupPortalMember;
  onSaved: (group: ProspectGroupPortalData) => void;
  onError: (message: string) => void;
}) {
  const getToken = useTenantApiToken();
  const [email, setEmail] = useState(member.primaryEmail ?? '');
  const [canadianDraft, setCanadianDraft] = useState('');
  useEffect(() => {
    setEmail(member.primaryEmail ?? '');
  }, [member.primaryEmail]);

  return (
    <UntestablePhoneContact
      name={member.displayName || 'Group member'}
      phones={phonesForMember(member)}
      emailValue={email}
      onEmailChange={setEmail}
      onEmailCommit={() => {
        const next = email.trim();
        if (!next || next === (member.primaryEmail ?? '').trim()) return;
        void putProspectGroupMemberEmail(getToken, member.id, next)
          .then((result) => {
            onSaved(result.group);
            if (result.inviteEmailError) onError(result.inviteEmailError);
          })
          .catch((err: unknown) => {
            setEmail(member.primaryEmail ?? '');
            onError(err instanceof Error ? err.message : 'Could not save that email');
          });
      }}
      canadianDraft={canadianDraft}
      onCanadianDraftChange={setCanadianDraft}
      onCanadianCommit={() => {
        const next = canadianDraft.trim();
        if (!next) return;
        if (!isCanadianPhone(next)) {
          onError('Enter a Canadian phone number.');
          return;
        }
        void putProspectGroupMemberPhone(getToken, member.id, next)
          .then((updated) => {
            setCanadianDraft('');
            onSaved(updated);
          })
          .catch((err: unknown) => {
            onError(err instanceof Error ? err.message : 'Could not save that phone number');
          });
      }}
    />
  );
}

function DraftContactCell({
  row,
  rowError,
  onChange,
  onClearError,
}: {
  row: DraftRow;
  rowError?: string;
  onChange: (next: DraftRow) => void;
  onClearError: () => void;
}) {
  const [phoneDraft, setPhoneDraft] = useState(row.phone);
  const [canadianDraft, setCanadianDraft] = useState(row.canadianPhone);
  const [emailDraft, setEmailDraft] = useState(row.email);
  const [phoneFocused, setPhoneFocused] = useState(false);
  const [mode, setMode] = useState<'phone' | 'email'>('phone');

  useEffect(() => {
    if (!phoneFocused) {
      setPhoneDraft(row.phone);
      setCanadianDraft(row.canadianPhone);
      setEmailDraft(row.email);
    }
  }, [row.phone, row.canadianPhone, row.email, row.clientId, phoneFocused]);

  const commitContactFields = () => {
    const digits = phoneDraft.replace(/\D/g, '').length;
    const phoneKind = loginPhoneKind(phoneDraft);
    if (digits > 0 && phoneKind === 'incomplete') {
      setPhoneDraft(row.phone);
      setCanadianDraft(row.canadianPhone);
      setEmailDraft(row.email);
      return;
    }
    const next = {
      ...row,
      phone: phoneDraft,
      canadianPhone: canadianDraft,
      email: emailDraft,
    };
    if (
      next.phone === row.phone &&
      next.canadianPhone === row.canadianPhone &&
      next.email === row.email
    ) {
      return;
    }
    onChange(next);
  };

  const showUntextableExtras =
    !phoneFocused &&
    phoneCannotBeTexted(row.phone) &&
    !isCanadianPhone(row.canadianPhone);

  const phoneFieldClass = `${GROUP_FIELD_CLASS} tabular-nums${rowError ? ' border-red-400 ring-1 ring-red-200' : ''}`;

  const primaryPhoneInput = (
    <input
      placeholder="Phone"
      aria-label="Phone"
      inputMode="tel"
      autoComplete="tel"
      value={phoneDraft}
      onFocus={() => {
        setPhoneFocused(true);
        if (rowError) onClearError();
      }}
      onBlur={() => {
        setPhoneFocused(false);
        commitContactFields();
      }}
      onChange={(event) => {
        setPhoneDraft(formatPhoneInputLive(event.target.value));
        if (rowError) onClearError();
      }}
      aria-invalid={rowError ? true : undefined}
      className={phoneFieldClass}
    />
  );

  if (!showUntextableExtras) {
    return (
      <LabeledCell label="Phone" wide spanAll>
        {primaryPhoneInput}
      </LabeledCell>
    );
  }

  return (
    <LabeledCell label={mode === 'email' ? 'Email' : 'Phone'} wide spanAll>
      <div className="flex items-start gap-1">
        <div className="min-w-0 flex-1 space-y-1">
          {mode === 'phone' ? (
            <>
              {primaryPhoneInput}
              <SmsBlockedNotice />
              <input
                placeholder="Canadian number"
                aria-label="Canadian phone number"
                inputMode="tel"
                autoComplete="tel"
                value={canadianDraft}
                onChange={(event) => setCanadianDraft(formatPhoneInputLive(event.target.value))}
                onBlur={commitContactFields}
                className={`${GROUP_FIELD_CLASS} tabular-nums`}
              />
            </>
          ) : (
            <>
              <input
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="Email"
                aria-label="Email"
                value={emailDraft}
                onChange={(event) => setEmailDraft(event.target.value)}
                onBlur={commitContactFields}
                className={GROUP_FIELD_CLASS}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400">We'll email the group invite to this address.</p>
            </>
          )}
        </div>
        <button
          type="button"
          className={CONTACT_TOGGLE_CLASS}
          onClick={() => setMode((current) => (current === 'phone' ? 'email' : 'phone'))}
        >
          {mode === 'phone' ? 'Email' : 'Phone'}
        </button>
      </div>
    </LabeledCell>
  );
}

export interface ProspectGroupMembership {
  hasAddedPerson: boolean;
  isLead: boolean;
  pending: boolean;
}

interface ProspectGroupSectionProps {
  /** Renders inside My Information card (no separate outer panel). */
  embedded?: boolean;
  /** Replaces the embedded section chrome when the parent already supplies the card. */
  embeddedClassName?: string;
  onSaveActivity?: (state: 'idle' | 'saving' | 'saved') => void;
  onMembership?: (membership: ProspectGroupMembership) => void;
  complete?: boolean;
}

const GROUP_OUTLINE_BUTTON =
  'inline-flex items-center justify-center gap-1.5 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-800 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/15 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-white/5';

const GROUP_MUTED_BUTTON =
  'inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-600 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/10 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-white/5';

function GroupActionFooter({
  showAddAnother,
  onAddAnother,
  soloDeclared,
  canDeclareSolo,
  soloSaving,
  onDeclareSolo,
  onUndoSolo,
  showLeave,
  leaving,
  onLeave,
}: {
  showAddAnother: boolean;
  onAddAnother: () => void;
  soloDeclared: boolean;
  canDeclareSolo: boolean;
  soloSaving: boolean;
  onDeclareSolo: () => void;
  onUndoSolo: () => void;
  showLeave: boolean;
  leaving: boolean;
  onLeave: () => void;
}) {
  const showSolo = soloDeclared || canDeclareSolo;
  if (!showAddAnother && !showSolo && !showLeave) return null;

  return (
    <div className="flex flex-col gap-3 pt-1 sm:flex-row sm:items-center sm:justify-between">
      {showAddAnother ? (
        <button
          type="button"
          onClick={onAddAnother}
          className="inline-flex items-center gap-1 self-start text-sm font-medium text-red-700 hover:text-red-800 dark:text-red-400"
        >
          <Plus className="h-4 w-4" />
          Add another person
        </button>
      ) : (
        <span className="hidden min-w-0 flex-1 sm:block" aria-hidden="true" />
      )}
      <div className="flex flex-col gap-2 sm:ml-auto sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
        {soloDeclared ? (
          <>
            <p className="text-sm text-gray-700 dark:text-gray-200 sm:mr-1">You're renting on your own.</p>
            <button type="button" disabled={soloSaving} onClick={onUndoSolo} className={GROUP_OUTLINE_BUTTON}>
              <Plus className="h-4 w-4" />
              Add a roommate
            </button>
          </>
        ) : canDeclareSolo ? (
          <button type="button" disabled={soloSaving} onClick={onDeclareSolo} className={GROUP_OUTLINE_BUTTON}>
            {soloSaving ? 'Saving…' : "I'm renting on my own"}
          </button>
        ) : null}
        {showLeave ? (
          <button type="button" disabled={leaving} onClick={onLeave} className={GROUP_MUTED_BUTTON}>
            {leaving ? 'Leaving…' : 'Leave this group'}
          </button>
        ) : null}
      </div>
    </div>
  );
}

function groupHasAddedPerson(
  group: ProspectGroupPortalData | null,
  selfPersonId: string | null | undefined,
): boolean {
  if (group?.soloDeclaredAt) return true;
  return Boolean(
    group?.members.some(
      (member) =>
        member.personId !== selfPersonId &&
        (member.status === 'INVITED' || member.status === 'ACCEPTED'),
    ),
  );
}

export default function ProspectGroupSection({
  embedded = false,
  embeddedClassName,
  onSaveActivity,
  onMembership,
  complete = false,
}: ProspectGroupSectionProps) {
  const { prospect } = useTenantProfile();
  const getToken = useTenantApiToken();
  const initialGroup = prospect?.prospectGroup ?? null;
  const [group, setGroup] = useState<ProspectGroupPortalData | null>(initialGroup);
  const [loading, setLoading] = useState(!initialGroup);
  const [error, setError] = useState<string | null>(null);
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [finalizing, setFinalizing] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [handoffOpen, setHandoffOpen] = useState(false);
  const [successorPersonId, setSuccessorPersonId] = useState<string | null>(null);
  const [soloSaving, setSoloSaving] = useState(false);
  const [draftRows, setDraftRows] = useState<DraftRow[]>(() =>
    initialGroup ? draftInviteeRowsFromGroup(initialGroup) : [emptyRow()],
  );
  /** Prevents autosave from wiping invitees before GET / draft rows are applied (e.g. on reload). */
  const [draftReady, setDraftReady] = useState(Boolean(initialGroup));
  const draftHydratedRef = useRef(Boolean(initialGroup));
  const groupAutosaveRef = useRef<AutosaveControls>({
    status: 'idle',
    error: null,
    flush: async () => undefined,
    acknowledge: () => undefined,
  });
  const lastPersistedSnapshotRef = useRef(
    initialGroup
      ? serializeInviteRowsSnapshot(draftInviteeRowsFromGroup(initialGroup), initialGroup)
      : '[]',
  );
  const prospectRef = useRef(prospect);
  prospectRef.current = prospect;

  const isDraft = group?.status === 'DRAFT';
  const isHost = group?.isHostManager ?? false;
  const canEditDraft = Boolean(group?.canEditDraft ?? (isDraft && isHost));

  const selfIdentity = useMemo(() => {
    const selfMember = group?.members.find((member) => member.personId === prospect?.personId);
    const { firstName, lastName } = splitDisplayName(prospect?.displayName || selfMember?.displayName);
    return {
      firstName,
      lastName,
      phone: selfMember?.primaryPhone ?? prospect?.phone ?? '',
      personId: selfMember?.personId ?? prospect?.personId ?? null,
      isLead: selfMember ? selfMember.isHostManager : isHost,
      statusLabel: statusLabelForPerson(selfMember?.status, {
        isSelf: true,
        hasName: Boolean(firstName || lastName),
      }),
    };
  }, [group?.members, isHost, prospect?.displayName, prospect?.personId, prospect?.phone]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchProspectGroup(getToken);
      applyGroupDraftState(data, setGroup, setDraftRows);
      const loadedSnapshot = serializeInviteRowsSnapshot(draftInviteeRowsFromGroup(data), data);
      lastPersistedSnapshotRef.current = loadedSnapshot;
      draftHydratedRef.current = true;
      setDraftReady(true);
      groupAutosaveRef.current.acknowledge(loadedSnapshot);
    } catch (err) {
      const fallbackGroup = prospectRef.current?.prospectGroup;
      if (fallbackGroup) {
        applyGroupDraftState(fallbackGroup, setGroup, setDraftRows);
        const loadedSnapshot = serializeInviteRowsSnapshot(
          draftInviteeRowsFromGroup(fallbackGroup),
          fallbackGroup,
        );
        lastPersistedSnapshotRef.current = loadedSnapshot;
        draftHydratedRef.current = true;
        setDraftReady(true);
        groupAutosaveRef.current.acknowledge(loadedSnapshot);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to load your group');
      }
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    void load();
  }, [load, prospect?.phone]);

  useEffect(() => {
    onSaveActivity?.(saveState);
  }, [onSaveActivity, saveState]);

  useEffect(() => {
    if (loading) return;
    onMembership?.({
      isLead: Boolean(group?.isHostManager),
      hasAddedPerson: groupHasAddedPerson(group, prospect?.personId),
      pending: Boolean(
        canEditDraft &&
          serializeInviteRowsSnapshot(draftRows, group) !== lastPersistedSnapshotRef.current,
      ),
    });
  }, [canEditDraft, draftRows, group, loading, onMembership, prospect?.personId, saveState]);

  const persistDraft = useCallback(
    async (options?: { force?: boolean }): Promise<ProspectGroupPortalData | null> => {
      if (!canEditDraft) return null;

      const snapshot = serializeInviteRowsSnapshot(draftRows, group);
      if (!options?.force && snapshot === lastPersistedSnapshotRef.current) return group;

      setSaveState('saving');
      setError(null);
      setRowErrors({});
      try {
        const result = await putProspectGroupDraft(getToken, {
          inviteRows: inviteRowsForPersist(draftRows, group),
        });
        const nextRows = draftInviteeRowsFromGroup(result.group);
        setDraftRows(nextRows);
        const nextSnapshot = serializeInviteRowsSnapshot(nextRows, result.group);
        setGroup(result.group);
        lastPersistedSnapshotRef.current = nextSnapshot;
        groupAutosaveRef.current.acknowledge(nextSnapshot);
        setSaveState('saved');
        window.setTimeout(() => setSaveState('idle'), 2000);
        if (result.inviteEmailError) setError(result.inviteEmailError);
        return result.group;
      } catch (err) {
        setSaveState('idle');
        const { sectionError, rowErrors: nextRowErrors } = applyProspectGroupErrorToRows(err, draftRows);
        setRowErrors(nextRowErrors);
        setError(sectionError);
        return null;
      }
    },
    [canEditDraft, draftRows, getToken, group],
  );

  const groupSnapshot = serializeInviteRowsSnapshot(draftRows, group);
  const groupAutosave = useAutosave({
    snapshot: groupSnapshot,
    ready: draftReady && !loading,
    enabled: canEditDraft,
    resetKey: group?.id ?? null,
    baselineSnapshot: lastPersistedSnapshotRef.current,
    save: async (snapshot, context) => {
      const inviteRows = JSON.parse(snapshot) as ReturnType<typeof inviteRowsForPersist>;
      const result = await putProspectGroupDraft(getToken, { inviteRows }, { keepalive: context.keepalive });
      const nextRows = draftInviteeRowsFromGroup(result.group);
      setDraftRows(nextRows);
      setGroup(result.group);
      lastPersistedSnapshotRef.current = serializeInviteRowsSnapshot(nextRows, result.group);
      setRowErrors({});
      setError(result.inviteEmailError);
    },
    onError: (err) => {
      const { sectionError, rowErrors: nextRowErrors } = applyProspectGroupErrorToRows(err, draftRows);
      setRowErrors(nextRowErrors);
      setError(sectionError);
    },
  });
  groupAutosaveRef.current = groupAutosave;

  useEffect(() => {
    if (groupAutosave.status === 'saving') setSaveState('saving');
    else if (groupAutosave.status === 'saved') setSaveState('saved');
    else if (groupAutosave.status === 'error') setSaveState('idle');
  }, [groupAutosave.status]);

  const groupSave = useCardSaveBadges(
    { group: groupSnapshot },
    groupAutosave.status,
    draftReady && !loading,
    group?.id ?? null,
    { group: lastPersistedSnapshotRef.current },
  );

  const onFinalize = async () => {
    setFinalizing(true);
    setError(null);
    try {
      if (hasPartialInviteeRows(draftRows)) {
        setError('Finish each invite (first name, last name, and phone) before sending.');
        return;
      }
      const saved = await persistDraft({ force: true });
      if (!saved) return;
      const updated = await finalizeProspectGroup(getToken);
      setGroup(updated);
      const nextRows = draftInviteeRowsFromGroup(updated);
      setDraftRows(nextRows);
      const nextSnapshot = serializeInviteRowsSnapshot(nextRows, updated);
      lastPersistedSnapshotRef.current = nextSnapshot;
      groupAutosaveRef.current.acknowledge(nextSnapshot);
    } catch (err) {
      const { sectionError, rowErrors: nextRowErrors } = applyProspectGroupErrorToRows(err, draftRows);
      setRowErrors(nextRowErrors);
      setError(sectionError ?? (err instanceof Error ? err.message : 'Could not finalize group'));
    } finally {
      setFinalizing(false);
    }
  };

  const onLeave = async () => {
    setError(null);
    if (selfIdentity.isLead) {
      let current = group;
      if (canEditDraft) {
        const saved = await persistDraft({ force: true });
        if (!saved) return;
        current = saved;
      }
      const candidates = (current?.members ?? []).filter(
        (member) => member.status !== 'REMOVED' && member.personId !== selfIdentity.personId,
      );
      if (candidates.length === 0) {
        setError('Add someone else to the group before you leave, so you can choose the next lead contact.');
        return;
      }
      setSuccessorPersonId(null);
      setHandoffOpen(true);
      return;
    }
    if (!window.confirm('Leave this group? You’ll be on your own in a new group.')) return;
    await completeLeave();
  };

  const completeLeave = async (successorPersonId?: string) => {
    setLeaving(true);
    setError(null);
    try {
      const updated = await leaveProspectGroup(getToken, successorPersonId);
      setHandoffOpen(false);
      setSuccessorPersonId(null);
      applyGroupDraftState(updated, setGroup, setDraftRows);
      const leftSnapshot = serializeInviteRowsSnapshot(
        draftInviteeRowsFromGroup(updated),
        updated,
      );
      lastPersistedSnapshotRef.current = leftSnapshot;
      draftHydratedRef.current = true;
      groupAutosaveRef.current.acknowledge(leftSnapshot);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not leave group');
    } finally {
      setLeaving(false);
    }
  };

  const onRemoveMember = async (memberId: string) => {
    setError(null);
    try {
      const updated = await removeProspectGroupMember(getToken, memberId);
      setGroup(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not remove member');
    }
  };

  const onSetLead = async (personId: string) => {
    setError(null);
    try {
      const updated = await setProspectGroupLeadContact(getToken, personId);
      setGroup(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not change the lead contact');
    }
  };

  const onSetLeadForDraftRow = async (row: DraftRow) => {
    setError(null);
    const matched = memberMatchingDraftRow(group, row);
    if (matched?.personId) {
      await onSetLead(matched.personId);
      return;
    }
    if (!rowIsComplete(row)) {
      setError('Add a first name, last name, and phone number before choosing a lead contact.');
      return;
    }
    const saved = await persistDraft({ force: true });
    const personId = memberMatchingDraftRow(saved, row)?.personId;
    if (!personId) {
      setError('Save this person before choosing them as the lead contact.');
      return;
    }
    await onSetLead(personId);
  };

  const setSoloHousehold = async (declared: boolean) => {
    setSoloSaving(true);
    setError(null);
    try {
      const updated = await putProspectGroupSoloDeclared(getToken, declared);
      applyGroupDraftState(updated, setGroup, setDraftRows);
      const soloSnapshot = serializeInviteRowsSnapshot(
        draftInviteeRowsFromGroup(updated),
        updated,
      );
      lastPersistedSnapshotRef.current = soloSnapshot;
      groupAutosaveRef.current.acknowledge(soloSnapshot);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save that choice.');
    } finally {
      setSoloSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={embedded ? 'flex justify-center py-6' : 'mt-6 flex justify-center py-8'}>
        <Loader2 className="h-7 w-7 animate-spin text-red-700 dark:text-red-400" />
      </div>
    );
  }

  if (!group) return null;

  const soloDeclared = Boolean(group.soloDeclaredAt);
  const activeMemberCount = group.members.filter((member) => member.status !== 'REMOVED').length;
  const hasOtherPeople =
    activeMemberCount > 1 || draftRows.length > 1 || draftRows.some((row) => rowHasAnyContent(row));
  const canDeclareSolo = canEditDraft && !soloDeclared && !hasOtherPeople;
  const showLeaveGroup = !handoffOpen && activeMemberCount > 1;

  const header = (
    <div className={embedded ? 'mb-4' : 'border-b border-gray-100 dark:border-white/5 px-4 py-4 sm:px-6'}>
      <h2 className="pr-8 text-lg font-bold text-gray-900 dark:text-white">My group</h2>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        Do you know who you will be living with already? If so, add them now.
      </p>
    </div>
  );

  const body = (
    <div className={`space-y-4 ${embedded ? '' : 'px-4 py-4 sm:px-6 sm:py-5'}`}>
      {error && (
        <div className="rounded-lg border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-950/40 p-3 text-sm text-red-800">{error}</div>
      )}

      {canEditDraft && isDraft && (
        <>
          <GroupPeople>
            <p className="text-sm font-medium text-gray-800 dark:text-gray-100">People in this group</p>
            <GroupColumnHeader />
            <GroupTableRow>
              <PersonReadOnlyFields
                firstName={selfIdentity.firstName}
                lastName={selfIdentity.lastName}
                phone={selfIdentity.phone}
                name="Your"
              />
              <StatusCell label={selfIdentity.statusLabel} />
              <LeadContactCell
                isLead={selfIdentity.isLead}
                onSetLead={
                  selfIdentity.isLead || !selfIdentity.personId
                    ? undefined
                    : () => void onSetLead(selfIdentity.personId!)
                }
              />
            </GroupTableRow>
            {!soloDeclared && draftRows.map((row, index) => {
              const rowError = rowErrors[row.clientId];
              const entered = rowHasAnyContent(row);
              const matched = memberMatchingDraftRow(group, row);
              const isLead = Boolean(matched?.isHostManager);
              const displayName = [row.firstName, row.lastName].filter(Boolean).join(' ') || 'this person';
              const showRemove = entered || draftRows.length > 1;
              return (
                <div key={row.clientId} className="space-y-1">
                  <GroupTableRow
                    remove={
                      showRemove ? (
                        <RemoveFromGroupButton
                          name={displayName}
                          onClick={() => setDraftRows(draftRows.filter((_, i) => i !== index))}
                        />
                      ) : null
                    }
                  >
                    <LabeledCell label="First name" wide>
                      <input
                        placeholder="First name"
                        aria-label="First name"
                        value={row.firstName}
                        onChange={(e) => {
                          const next = [...draftRows];
                          next[index] = { ...row, firstName: e.target.value };
                          setDraftRows(next);
                        }}
                        className={GROUP_FIELD_CLASS}
                      />
                    </LabeledCell>
                    <LabeledCell label="Last name" wide>
                      <input
                        placeholder="Last name"
                        aria-label="Last name"
                        value={row.lastName}
                        onChange={(e) => {
                          const next = [...draftRows];
                          next[index] = { ...row, lastName: e.target.value };
                          setDraftRows(next);
                        }}
                        className={GROUP_FIELD_CLASS}
                      />
                    </LabeledCell>
                    <DraftContactCell
                      row={row}
                      rowError={rowError}
                      onChange={(next) => {
                        const rows = [...draftRows];
                        rows[index] = next;
                        setDraftRows(rows);
                      }}
                      onClearError={() => {
                        setError(null);
                        setRowErrors((prev) => {
                          const { [row.clientId]: _, ...rest } = prev;
                          return rest;
                        });
                      }}
                    />
                    {rowIsComplete(row) && !rowNeedsEmail(row) ? (
                      <StatusCell label={null} onSend={() => void onFinalize()} sending={finalizing} />
                    ) : (
                      <StatusCell label={rowHasName(row) ? 'Unsent' : null} />
                    )}
                    <LeadContactCell
                      isLead={isLead}
                      onSetLead={entered && !isLead ? () => void onSetLeadForDraftRow(row) : undefined}
                    />
                  </GroupTableRow>
                  {rowError && (
                    <p className="text-sm text-red-700 dark:text-red-400" role="alert">
                      {rowError}
                    </p>
                  )}
                </div>
              );
            })}
          </GroupPeople>
          <GroupActionFooter
            showAddAnother={!soloDeclared}
            onAddAnother={() => setDraftRows([...draftRows, emptyRow()])}
            soloDeclared={soloDeclared}
            canDeclareSolo={canDeclareSolo}
            soloSaving={soloSaving}
            onDeclareSolo={() => void setSoloHousehold(true)}
            onUndoSolo={() => void setSoloHousehold(false)}
            showLeave={showLeaveGroup}
            leaving={leaving}
            onLeave={() => void onLeave()}
          />
        </>
      )}

      {(group.status === 'ACTIVE' || (isDraft && !canEditDraft)) && (
        <GroupPeople>
          <p className="text-sm font-medium text-gray-800 dark:text-gray-100">People in this group</p>
          <GroupColumnHeader />
          {group.members
            .filter(
              (m) =>
                m.status !== 'REMOVED' &&
                !(
                  group.status === 'ACTIVE' &&
                  canEditDraft &&
                  (m.status === 'DRAFT' || m.status === 'INVITED')
                ),
            )
            .map((member) => {
              const isSelf = member.personId === prospect?.personId;
              const { firstName, lastName } = splitDisplayName(
                (isSelf ? prospect?.displayName : undefined) || member.displayName,
              );
              const canRemove = !isSelf && member.status !== 'REMOVED';
              const canEditContact = canEditDraft || isSelf;
              return (
                <GroupTableRow
                  key={member.id}
                  remove={
                    canRemove ? (
                      <RemoveFromGroupButton
                        name={member.displayName || 'this person'}
                        onClick={() => void onRemoveMember(member.id)}
                      />
                    ) : null
                  }
                >
                  <PersonReadOnlyFields
                    firstName={firstName}
                    lastName={lastName}
                    phone={member.primaryPhone ?? ''}
                    name={member.displayName || 'Group member'}
                    hidePhone
                  />
                  {canEditContact ? (
                    <MemberUntestableContact
                      member={member}
                      onSaved={(updated) => {
                        setGroup(updated);
                        setError(null);
                      }}
                      onError={setError}
                    />
                  ) : (
                    <UntestablePhoneContact
                      name={member.displayName || 'Group member'}
                      phones={phonesForMember(member)}
                      emailValue={member.primaryEmail ?? ''}
                      onEmailChange={() => undefined}
                      onEmailCommit={() => undefined}
                      canadianDraft=""
                      onCanadianDraftChange={() => undefined}
                      onCanadianCommit={() => undefined}
                      readOnly
                    />
                  )}
                  <StatusCell
                    label={statusLabelForPerson(member.status, {
                      isSelf,
                      hasName: Boolean(firstName || lastName || member.displayName?.trim()),
                    })}
                  />
                  <LeadContactCell
                    isLead={member.isHostManager}
                    onSetLead={
                      member.isHostManager ? undefined : () => void onSetLead(member.personId)
                    }
                  />
                </GroupTableRow>
              );
            })}
        </GroupPeople>
      )}

      {canEditDraft && !isDraft && (
        <>
          <GroupPeople>
            <div className="border-t border-gray-100 dark:border-white/5 pt-4" />
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Pending invites stay editable here — fix a wrong phone or email before they accept.
            </p>
            <GroupColumnHeader />
            {!soloDeclared && draftRows.map((row, index) => {
              const rowError = rowErrors[row.clientId];
              const entered = rowHasAnyContent(row);
              const displayName = [row.firstName, row.lastName].filter(Boolean).join(' ') || 'this person';
              const showRemove = entered || draftRows.length > 1;
              const linkedMember = memberMatchingDraftRow(group, row);
              const inviteAlreadySent = linkedMember?.status === 'INVITED';
              return (
                <div key={row.clientId} className="space-y-1">
                  <GroupTableRow
                    remove={
                      showRemove ? (
                        <RemoveFromGroupButton
                          name={displayName}
                          onClick={() => setDraftRows(draftRows.filter((_, i) => i !== index))}
                        />
                      ) : null
                    }
                  >
                    <LabeledCell label="First name" wide>
                      <input
                        placeholder="First name"
                        aria-label="First name"
                        value={row.firstName}
                        onChange={(e) => {
                          const next = [...draftRows];
                          next[index] = { ...row, firstName: e.target.value };
                          setDraftRows(next);
                        }}
                        className={GROUP_FIELD_CLASS}
                      />
                    </LabeledCell>
                    <LabeledCell label="Last name" wide>
                      <input
                        placeholder="Last name"
                        aria-label="Last name"
                        value={row.lastName}
                        onChange={(e) => {
                          const next = [...draftRows];
                          next[index] = { ...row, lastName: e.target.value };
                          setDraftRows(next);
                        }}
                        className={GROUP_FIELD_CLASS}
                      />
                    </LabeledCell>
                    <DraftContactCell
                      row={row}
                      rowError={rowError}
                      onChange={(next) => {
                        const rows = [...draftRows];
                        rows[index] = next;
                        setDraftRows(rows);
                      }}
                      onClearError={() => {
                        setError(null);
                        setRowErrors((prev) => {
                          const { [row.clientId]: _, ...rest } = prev;
                          return rest;
                        });
                      }}
                    />
                    {rowIsComplete(row) && !rowNeedsEmail(row) && !inviteAlreadySent ? (
                      <StatusCell label={null} onSend={() => void onFinalize()} sending={finalizing} />
                    ) : (
                      <StatusCell
                        label={
                          inviteAlreadySent ? 'Pending' : rowHasName(row) ? 'Unsent' : null
                        }
                      />
                    )}
                    <LeadContactCell isLead={false} />
                  </GroupTableRow>
                  {rowError && (
                    <p className="text-sm text-red-700 dark:text-red-400" role="alert">
                      {rowError}
                    </p>
                  )}
                </div>
              );
            })}
          </GroupPeople>
          <GroupActionFooter
            showAddAnother={!soloDeclared}
            onAddAnother={() => setDraftRows([...draftRows, emptyRow()])}
            soloDeclared={soloDeclared}
            canDeclareSolo={canDeclareSolo}
            soloSaving={soloSaving}
            onDeclareSolo={() => void setSoloHousehold(true)}
            onUndoSolo={() => void setSoloHousehold(false)}
            showLeave={showLeaveGroup}
            leaving={leaving}
            onLeave={() => void onLeave()}
          />
        </>
      )}

      {handoffOpen && (
        <div className="rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 p-4">
          <p className="text-sm font-medium text-gray-900 dark:text-white">Choose the new lead contact</p>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            You’re the lead contact. Pick who should take that role in this group. After you leave, you’ll be on
            your own in a new group.
          </p>
          <div className="mt-3 space-y-2">
            {group.members
              .filter((member) => member.status !== 'REMOVED' && member.personId !== selfIdentity.personId)
              .map((member) => (
                <label key={member.personId} className="flex items-center gap-2 text-sm text-gray-800 dark:text-gray-100">
                  <input
                    type="radio"
                    name="prospect-group-successor"
                    value={member.personId}
                    checked={successorPersonId === member.personId}
                    onChange={() => setSuccessorPersonId(member.personId)}
                  />
                  <span>
                    {member.displayName}
                    {member.primaryPhone
                      ? ` · ${formatPhoneForTenantPortalDisplay(member.primaryPhone)}`
                      : ''}
                  </span>
                </label>
              ))}
          </div>
          <div className="mt-4 flex flex-wrap gap-3">
            <button
              type="button"
              disabled={leaving || !successorPersonId}
              onClick={() => void completeLeave(successorPersonId ?? undefined)}
              className="rounded-lg bg-red-700 px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              {leaving ? 'Leaving…' : 'Leave group'}
            </button>
            <button
              type="button"
              disabled={leaving}
              onClick={() => {
                setHandoffOpen(false);
                setSuccessorPersonId(null);
              }}
              className="text-sm font-medium text-gray-500 dark:text-gray-400 underline hover:text-gray-700 dark:hover:text-gray-200"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {!canEditDraft && !isDraft && showLeaveGroup && (
        <GroupActionFooter
          showAddAnother={false}
          onAddAnother={() => {}}
          soloDeclared={soloDeclared}
          canDeclareSolo={false}
          soloSaving={false}
          onDeclareSolo={() => {}}
          onUndoSolo={() => {}}
          showLeave={showLeaveGroup}
          leaving={leaving}
          onLeave={() => void onLeave()}
        />
      )}
    </div>
  );

  if (embedded) {
    return (
      <section
        className={embeddedClassName ?? 'border-t border-gray-100 dark:border-white/5 pt-6 sm:pt-8'}
        aria-labelledby="prospect-my-group-heading"
      >
        <CardSaveBadge phase={groupSave.group} complete={complete} />
        <div id="prospect-my-group-heading">{header}</div>
        {body}
      </section>
    );
  }

  return (
    <section className="relative mt-8 rounded-2xl border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-800 shadow-bento">
      <CardSaveBadge phase={groupSave.group} complete={complete} />
      {header}
      {body}
    </section>
  );
}
